<template>
  <div class="basket-proposal-list">
    <h1 data-test-proposal-list-title v-html="title" />

    <div
      v-if="!areProposalsEmpty && !isLoading"
      class="basket-proposal-list__items"
      data-test-proposal-list-items
    >
      <basket-proposal-item
        v-for="(item, index) in proposals"
        :key="item.id"
        :proposal="item"
        :index="index"
        :covers-label="coversLabel"
        :per-year-label="perYearLabel"
        :deductible-label="deductibleLabel"
        :clauses-label="clausesLabel"
        :monthly-payment-label="monthlyPaymentLabel"
        :half-yearly-payment-label="halfYearlyPaymentLabel"
        :quarterly-payment-label="quarterlyPaymentLabel"
        :remove-proposal-label="removeProposalLabel"
        :download-proposal-label="downloadProposalLabel"
        :selected="isProposalSelected(item.id)"
        :data-test="'proposal-list-item-' + item.id"
        @remove-proposal="removeFromBasketInitiated"
        @checkbox-click="handleProposalSelectChange"
        @payment-interval-changed="handlePaymentIntervalChanged"
      />
    </div>
    <h2
      v-if="areProposalsEmpty && !isLoading"
      class="basket-proposal-list__no-items"
      data-test-basket-proposal-list-no-items
      v-html="noItemsLabel"
    />
    <button
      data-test-proposal-list-add-button
      class="large-6 basket-proposal-list__add-policy button tertiary"
      @click="$emit('add-proposal')"
    >
      <span v-html="addProposalButtonLabel" />
    </button>
    <remove-proposal-modal
      data-test-remove-proposal-modal
      :open="removeProposalModalOpen"
      :headline="removeModalHeadline"
      :yes-label="removeModalYesLabel"
      :no-label="removeModalNoLabel"
      @proposal-remove-confirm="removeFromBasketConfirm"
      @proposal-remove-cancel="removeFromBasketCancel"
    />
  </div>
</template>
<script lang="ts">
import BasketProposalItem from '@/components/Feature/Basket/BasketProposalItem.vue';
import RemoveProposalModal from '@/components/Feature/Basket/RemoveProposalModal.vue';
import { ProposalListData } from '../types/ProposalBasket';
import {
  BasketProposal,
  BasketProposalPaymentInterval,
  ProposalBasketStoreState
} from '@/store/modules/interfaces/ProposalBasketStoreState';
import { mapState } from 'vuex';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'ProposalList',
  components: {
    BasketProposalItem,
    RemoveProposalModal
  },
  props: {
    isLoading: {
      type: Boolean,
      default: () => false
    },
    title: {
      type: String,
      default: () => ''
    },
    addProposalButtonLabel: {
      type: String,
      default: () => ''
    },
    coversLabel: {
      type: String,
      default: () => ''
    },
    perYearLabel: {
      type: String,
      default: () => ''
    },
    deductibleLabel: {
      type: String,
      default: () => ''
    },
    noItemsLabel: {
      type: String,
      default: () => ''
    },
    removeModalHeadline: {
      type: String,
      default: () => ''
    },
    removeModalYesLabel: {
      type: String,
      default: () => ''
    },
    removeModalNoLabel: {
      type: String,
      default: () => ''
    },
    clausesLabel: {
      type: String,
      default: () => ''
    },
    halfYearlyPaymentLabel: {
      type: String,
      default: () => ''
    },
    quarterlyPaymentLabel: {
      type: String,
      default: () => ''
    },
    monthlyPaymentLabel: {
      type: String,
      default: () => ''
    },
    removeProposalLabel: {
      type: String,
      default: () => ''
    },
    downloadProposalLabel: {
      type: String,
      default: () => ''
    }
  },
  data(): ProposalListData {
    return {
      removeProposalModalOpen: false
    };
  },
  computed: {
    ...mapState('proposalBasket', {
      proposals: (state) => (state as ProposalBasketStoreState).proposals,
      selectedProposalIds: (state) => (state as ProposalBasketStoreState).selectedProposals
    }),
    areProposalsEmpty(): boolean {
      return !this.proposals || this.proposals.length === 0;
    }
  },
  methods: {
    removeFromBasketInitiated(proposal: BasketProposal): void {
      this.removeProposalModalOpen = true;
      this.proposalToRemove = proposal;
    },
    removeFromBasketConfirm() {
      this.$store.dispatch('proposalBasket/removeProposal', this.proposalToRemove);
      this.removeProposalModalOpen = false;
    },
    removeFromBasketCancel() {
      this.removeProposalModalOpen = false;
    },
    handleProposalSelectChange(proposal: BasketProposal, selected: boolean) {
      this.$store.dispatch('proposalBasket/selectOrDeselectProposal', { proposal, selected });
    },
    isProposalSelected(proposalId: string): boolean {
      return this.selectedProposalIds.includes(proposalId);
    },
    handlePaymentIntervalChanged(
      proposalId: string,
      paymentInterval: BasketProposalPaymentInterval,
      externalProposalId: string
    ) {
      this.$store.dispatch('proposalBasket/changeProposalPaymentInterval', {
        proposalId,
        paymentInterval,
        externalProposalId
      });
    }
  }
});
</script>
<style lang="scss" scoped>
@import 'src/styles/all.scss';

.basket-proposal-list {
  > h1 {
    margin-bottom: rem-calc(20);
  }

  &__items {
    grid-column: 1 / span 2;
  }

  &__no-items {
    grid-column: 1 / span 2;
    margin: rem-calc(20) 0;
  }

  &__add-policy {
    display: flex;
    justify-content: center;
    margin-top: rem-calc(40);

    > .icon {
      margin-right: rem-calc(8);
    }
  }

  margin-bottom: rem-calc(30);
}
</style>
